import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  deleteUser as fbDeleteUser,
  type User,
} from 'firebase/auth'
import {
  doc, getDoc, setDoc, updateDoc, serverTimestamp,
  arrayUnion, arrayRemove,
} from 'firebase/firestore'
import { auth, db } from './firebase'

export type Role = 'paciente' | 'medico'

/**
 * Beta: cadastro de médico era restrito. Restrição removida — qualquer usuário pode
 * se cadastrar como médico. Mantido helper exportado por compatibilidade.
 */
export const EMAILS_MEDICO_PERMITIDOS: string[] = []
export const CADASTRO_MEDICO_LIVRE = true

export type Perfil = {
  nome: string
  email: string
  role: Role
  // paciente
  dataNascimento?: string
  sexo?: 'masculino' | 'feminino' | 'outro'
  telefone?: string
  cpf?: string  // só dígitos
  /** N médicos vinculados */
  medicoUids?: string[]
  /** N convites pendentes (emails de médicos aguardando aceite) */
  medicoEmailsPendentes?: string[]
  // legacy (compat) — convertido em runtime
  medicoUid?: string | null
  medicoEmail?: string | null
  // medico
  crm?: string
  especialidade?: string
  clinicaNome?: string
  clinicaEndereco?: string
  clinicaCidade?: string
  clinicaUf?: string
  clinicaCep?: string
  clinicaSite?: string
  clinicaWhatsapp?: string
  clinicaInstagram?: string
  // commons
  onboardingCompleto?: boolean
  provider?: string
  /** Tokens FCM por dispositivo (id é o token, valor é metadata) */
  fcmTokens?: string[]
  // Assinatura (médico)
  assinatura?: import('./planos').Assinatura | null
  /** UID da clínica (PRO multi-médico) — quando não-PRO, médico é própria clínica */
  clinicaId?: string | null
}

type AuthCtx = {
  user: User | null
  perfil: Perfil | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  cadastrar: (nome: string, email: string, password: string, role?: Role) => Promise<void>
  loginGoogle: (role?: Role) => Promise<void>
  recuperarSenha: (email: string) => Promise<void>
  atualizarPerfil: (dados: Partial<Perfil>) => Promise<void>
  refreshPerfil: () => Promise<void>
  convidarMedico: (email: string) => Promise<void>
  cancelarConviteMedico: (email: string) => Promise<void>
  removerMedicoVinculado: (uid: string) => Promise<void>
  promoverParaMedico: () => Promise<void>
  sair: () => Promise<void>
  /** Marca conta pra deleção (requer reautenticação recente) */
  deletarConta: () => Promise<void>
  /** Reenvia email de verificação pro currentUser */
  reenviarEmailVerificacao: () => Promise<void>
}

const Ctx = createContext<AuthCtx | null>(null)

/** Normaliza perfil: copia legacy medicoUid/medicoEmail pros arrays se necessário. */
function normalizar(p: Perfil): Perfil {
  const medicoUids = Array.isArray(p.medicoUids) ? p.medicoUids.slice() : []
  const medicoEmailsPendentes = Array.isArray(p.medicoEmailsPendentes) ? p.medicoEmailsPendentes.slice() : []
  if (p.medicoUid && !medicoUids.includes(p.medicoUid)) {
    medicoUids.push(p.medicoUid)
  }
  if (p.medicoEmail && !medicoEmailsPendentes.includes(p.medicoEmail)) {
    medicoEmailsPendentes.push(p.medicoEmail)
  }
  return { ...p, medicoUids, medicoEmailsPendentes }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [perfil, setPerfil] = useState<Perfil | null>(null)
  const [loading, setLoading] = useState(true)

  async function carregarPerfil(uid: string): Promise<Perfil | null> {
    const snap = await getDoc(doc(db, 'users', uid))
    if (!snap.exists()) return null
    return normalizar(snap.data() as Perfil)
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u)
      if (u) {
        const p = await carregarPerfil(u.uid)
        setPerfil(p)
        // Contextualiza Sentry com uid + role pra debug de erros
        import('./sentry').then(({ setSentryUser }) => setSentryUser(u.uid, p?.role))
      } else {
        setPerfil(null)
        import('./sentry').then(({ setSentryUser }) => setSentryUser(null))
      }
      setLoading(false)
    })
    return unsub
  }, [])

  async function login(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password)
  }

  async function cadastrar(nome: string, email: string, password: string, role: Role = 'paciente') {
    const cred = await createUserWithEmailAndPassword(auth, email, password)
    await updateProfile(cred.user, { displayName: nome })
    await setDoc(doc(db, 'users', cred.user.uid), {
      nome,
      email,
      criadoEm: serverTimestamp(),
      role,
      onboardingCompleto: false,
      medicoUids: [],
      medicoEmailsPendentes: [],
    })
    // Dispara email de verificação via NOSSO backend (HTML custom em PT-BR via SendGrid).
    // Não bloqueia o cadastro se falhar (rede, etc).
    try {
      const token = await cred.user.getIdToken()
      const API_BASE = import.meta.env.VITE_API_BASE || 'https://anamis-api-639009499195.southamerica-east1.run.app'
      await fetch(`${API_BASE}/auth/email/verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch (e) {
      console.warn('[auth] enviar verificação backend', e)
    }
  }

  async function reenviarEmailVerificacao(): Promise<void> {
    const u = auth.currentUser
    if (!u) throw new Error('Não autenticado')
    if (u.emailVerified) return
    const token = await u.getIdToken()
    const API_BASE = import.meta.env.VITE_API_BASE || 'https://anamis-api-639009499195.southamerica-east1.run.app'
    const res = await fetch(`${API_BASE}/auth/email/verify`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      const e = new Error(data.detail || `Erro ${res.status}`) as Error & { code?: string }
      if (res.status === 429) e.code = 'auth/too-many-requests'
      throw e
    }
  }

  async function loginGoogle(role: Role = 'paciente') {
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })
    const cred = await signInWithPopup(auth, provider)
    const ref = doc(db, 'users', cred.user.uid)
    const snap = await getDoc(ref)
    if (!snap.exists()) {
      await setDoc(ref, {
        nome: cred.user.displayName || '',
        email: cred.user.email || '',
        criadoEm: serverTimestamp(),
        role,
        provider: 'google',
        onboardingCompleto: false,
        medicoUids: [],
        medicoEmailsPendentes: [],
      })
    } else {
      // Promove role se user solicitou medico via cadastro
      const data = snap.data() as Perfil
      if (role === 'medico' && data.role !== 'medico') {
        await updateDoc(ref, {
          role: 'medico',
          onboardingCompleto: false,
          atualizadoEm: serverTimestamp(),
        })
      }
    }
  }

  async function recuperarSenha(email: string) {
    // Usa nosso backend (HTML custom em PT-BR via SendGrid).
    // Backend retorna ok mesmo se email não existe (anti-enumeração) — comportamento esperado.
    const API_BASE = import.meta.env.VITE_API_BASE || 'https://anamis-api-639009499195.southamerica-east1.run.app'
    const res = await fetch(`${API_BASE}/auth/email/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.detail || `Erro ${res.status}`)
    }
  }

  async function atualizarPerfil(dados: Partial<Perfil>) {
    if (!user) throw new Error('Não autenticado')
    await updateDoc(doc(db, 'users', user.uid), {
      ...dados,
      atualizadoEm: serverTimestamp(),
    })
    if (dados.nome) await updateProfile(user, { displayName: dados.nome })
    const p = await carregarPerfil(user.uid)
    setPerfil(p)
  }

  async function refreshPerfil() {
    if (!user) return
    const p = await carregarPerfil(user.uid)
    setPerfil(p)
  }

  async function promoverParaMedico() {
    if (!user) throw new Error('Não autenticado')
    await updateDoc(doc(db, 'users', user.uid), {
      role: 'medico',
      onboardingCompleto: false,
      atualizadoEm: serverTimestamp(),
    })
    await refreshPerfil()
  }

  async function convidarMedico(email: string) {
    if (!user) throw new Error('Não autenticado')
    const e = email.trim().toLowerCase()
    await updateDoc(doc(db, 'users', user.uid), {
      medicoEmailsPendentes: arrayUnion(e),
      atualizadoEm: serverTimestamp(),
    })
    await refreshPerfil()
  }

  async function cancelarConviteMedico(email: string) {
    if (!user) throw new Error('Não autenticado')
    await updateDoc(doc(db, 'users', user.uid), {
      medicoEmailsPendentes: arrayRemove(email),
      atualizadoEm: serverTimestamp(),
    })
    await refreshPerfil()
  }

  async function removerMedicoVinculado(uid: string) {
    if (!user) throw new Error('Não autenticado')
    await updateDoc(doc(db, 'users', user.uid), {
      medicoUids: arrayRemove(uid),
      atualizadoEm: serverTimestamp(),
    })
    // tenta apagar o doc do médico (subcoleção) — se não tiver permissão, ignora
    try {
      const { deleteDoc } = await import('firebase/firestore')
      await deleteDoc(doc(db, 'users', uid, 'pacientes', user.uid))
    } catch {
      // médico não vai conseguir desvincular se não autorizado, mas array remove já reduz acesso
    }
    await refreshPerfil()
  }

  async function sair() {
    await signOut(auth)
    setPerfil(null)
  }

  async function deletarConta() {
    if (!user) throw new Error('Não autenticado')
    const { deletarMinhaContaServer } = await import('./api')
    await deletarMinhaContaServer()
    try {
      await fbDeleteUser(user)
    } catch (e: unknown) {
      const code = (e as { code?: string }).code
      if (code === 'auth/requires-recent-login') {
        throw new Error('Faça login novamente para confirmar a exclusão.')
      }
      throw e
    }
    setPerfil(null)
  }

  return (
    <Ctx.Provider
      value={{
        user, perfil, loading,
        login, cadastrar, loginGoogle, recuperarSenha,
        atualizarPerfil, refreshPerfil,
        convidarMedico, cancelarConviteMedico, removerMedicoVinculado,
        promoverParaMedico,
        sair, deletarConta,
        reenviarEmailVerificacao,
      }}
    >
      {children}
    </Ctx.Provider>
  )
}

export function useAuth() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth fora do AuthProvider')
  return v
}
